/**
 * Time Tracker Page — active timer + log history + timesheets.
 *
 * Tabs: Timer (start/stop active log) | Logs (historical list) | Timesheets (weekly).
 * Uses BLL routes: POST /v1/hros/time-logs/start, /stop, GET /v1/hros/timesheets.
 */

import * as React from 'react';
import {
  TimerWidget,
  ListPage,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Button,
  Badge,
} from '@yesbos/ui';
import { useNavigate } from 'react-router';
import { hrosApi } from '@/lib/api';

// ── BLL helper ────────────────────────────────────────────────────────────────

async function callBLL(path: string, method = 'POST', body?: unknown) {
  switch (method.toUpperCase()) {
    case 'GET': return hrosApi.get(path);
    case 'PUT': return hrosApi.put(path, body);
    case 'DELETE': return hrosApi.delete(path);
    default: return hrosApi.post(path, body);
  }
}

// ── Types ────────────────────────────────────────────────────────────────────

interface ActiveLog {
  id: string;
  start_time: string;
  description?: string;
}

interface Timesheet {
  id: string;
  employee_id: string;
  week_start: string;
  week_end: string;
  total_hours: number | null;
  status: string;
  submitted_at: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'secondary',
  submitted: 'warning',
  approved: 'success',
  rejected: 'destructive',
};

// ── Timesheets Tab ────────────────────────────────────────────────────────────

function TimesheetsTab() {
  const [sheets, setSheets] = React.useState<Timesheet[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await callBLL('/v1/hros/timesheets', 'GET');
      if (res.success) setSheets(res.data ?? []);
      else setError(res.error?.message ?? 'Failed to load timesheets');
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  async function handleSubmit(timesheetId: string) {
    setSubmitting(timesheetId);
    try {
      const res = await callBLL(`/v1/hros/timesheets/${timesheetId}/submit`, 'POST');
      if (res.success) {
        setSheets((prev) =>
          prev.map((s) =>
            s.id === timesheetId
              ? { ...s, status: 'submitted', submitted_at: new Date().toISOString() }
              : s,
          ),
        );
      } else {
        alert(res.error?.message ?? 'Failed to submit');
      }
    } catch {
      alert('Network error');
    } finally {
      setSubmitting(null);
    }
  }

  if (loading) {
    return (
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-24 rounded-lg border bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive flex items-center gap-2">
        <span>{error}</span>
        <Button size="sm" variant="outline" onClick={load}>Retry</Button>
      </div>
    );
  }

  if (!sheets.length) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground text-sm">
        No timesheets yet. Timesheets are auto-generated weekly from your time logs.
      </div>
    );
  }

  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
      {sheets.map((sheet) => (
        <div key={sheet.id} className="rounded-lg border bg-card p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">
              {sheet.week_start} — {sheet.week_end}
            </p>
            <Badge variant={(STATUS_COLORS[sheet.status] ?? 'secondary') as Parameters<typeof Badge>[0]['variant']}>
              {sheet.status}
            </Badge>
          </div>
          <p className="text-2xl font-bold">
            {sheet.total_hours != null ? `${sheet.total_hours}h` : '—'}
          </p>
          {sheet.submitted_at && (
            <p className="text-xs text-muted-foreground">
              Submitted {new Date(sheet.submitted_at).toLocaleDateString()}
            </p>
          )}
          {sheet.status === 'draft' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleSubmit(sheet.id)}
              disabled={submitting === sheet.id}
            >
              {submitting === sheet.id ? 'Submitting…' : 'Submit'}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function TimeTrackerListPage() {
  const navigate = useNavigate();
  const [activeLog, setActiveLog] = React.useState<ActiveLog | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [description, setDescription] = React.useState('');

  React.useEffect(() => {
    const stored = sessionStorage.getItem('hros_active_log');
    if (stored) {
      try { setActiveLog(JSON.parse(stored)); } catch { sessionStorage.removeItem('hros_active_log'); }
    }
  }, []);

  React.useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  async function handleStart() {
    setLoading(true);
    try {
      const res = await callBLL('/v1/hros/time-logs/start', 'POST', {
        description: description || undefined,
      });
      if (res.success) {
        const raw = res.data as Record<string, unknown>;
        const log: ActiveLog = {
          id: String(raw.id ?? ''),
          start_time: String(raw.start_time ?? new Date().toISOString()),
          description: description || undefined,
        };
        setActiveLog(log);
        sessionStorage.setItem('hros_active_log', JSON.stringify(log));
        setToast({ message: 'Timer started!', type: 'success' });
        setDescription('');
      } else {
        setToast({ message: res.error?.message ?? 'Failed to start timer.', type: 'error' });
      }
    } catch {
      setToast({ message: 'Network error.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  async function handleStop() {
    if (!activeLog) return;
    setLoading(true);
    try {
      const res = await callBLL('/v1/hros/time-logs/stop', 'POST', {
        log_id: activeLog.id,
        description: description || undefined,
      });
      if (res.success) {
        setActiveLog(null);
        sessionStorage.removeItem('hros_active_log');
        setToast({ message: 'Time logged successfully.', type: 'success' });
        setDescription('');
      } else {
        setToast({ message: res.error?.message ?? 'Failed to stop timer.', type: 'error' });
      }
    } catch {
      setToast({ message: 'Network error.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 rounded-md px-4 py-2 text-sm text-white shadow-lg ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Time Tracker</h1>
        <Button variant="outline" onClick={() => navigate('/time-tracker/create')}>
          + Manual Entry
        </Button>
      </div>

      <Tabs defaultValue="timer">
        <TabsList>
          <TabsTrigger value="timer">Timer</TabsTrigger>
          <TabsTrigger value="logs">All Logs</TabsTrigger>
          <TabsTrigger value="timesheets">Timesheets</TabsTrigger>
        </TabsList>

        <TabsContent value="timer" className="mt-4 space-y-4">
          {!activeLog && (
            <div className="max-w-sm">
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What are you working on? (optional)"
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          )}
          <div className="max-w-sm">
            <TimerWidget
              isCheckedIn={!!activeLog}
              checkinTime={activeLog?.start_time}
              onCheckin={loading ? () => {} : handleStart}
              onCheckout={loading ? () => {} : handleStop}
            />
          </div>
          {activeLog?.description && (
            <p className="text-sm text-muted-foreground">
              Tracking: <span className="font-medium text-foreground">{activeLog.description}</span>
            </p>
          )}
        </TabsContent>

        <TabsContent value="logs" className="mt-4">
          <ListPage
            collection="time_logs"
            title=""
            basePath="/time-tracker"
            excludeFields={['org_id', 'created_by', 'updated_at']}
            pageSize={25}
          />
        </TabsContent>

        <TabsContent value="timesheets" className="mt-4">
          <TimesheetsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default TimeTrackerListPage;
